import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../components/ui/select";
import {
  DollarSign, TrendingUp, Wallet, CheckCircle, Loader2, RefreshCw,
  CreditCard, Calendar, Receipt, BarChart3, ArrowDownRight, ArrowUpRight
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { useTimezone } from "../../contexts/TimezoneContext";

const DATE_FILTERS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "this_year", label: "This Year" },
  { value: "all", label: "All Time" },
];

function SummaryCard({ icon: Icon, label, value, color, bgColor, testId }) {
  return (
    <Card className="glass-card border-theme" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
            <p className={`text-xl font-bold ${color} font-['Manrope']`}>{value}</p>
          </div>
          <div className={`w-9 h-9 rounded-lg ${bgColor} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-4.5 h-4.5 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const TechFinancials = () => {
  const { timezone } = useTimezone();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState("this_month");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("tech_token") || localStorage.getItem("token");
      const res = await axios.get(`${API}/tech/financials`, {
        params: { date_filter: dateFilter, tz: timezone },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error("Failed to load financials:", err);
    } finally { setLoading(false); }
  }, [dateFilter, timezone]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const summary = data?.summary || {};
  const jobs = data?.jobs || [];
  const filterLabel = DATE_FILTERS.find(f => f.value === dateFilter)?.label || dateFilter;
  const fmt = (v) => `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const netColor = (summary.net_earnings || 0) >= 0 ? "text-emerald-400" : "text-red-400";
  const balColor = (summary.remaining_balance || 0) > 0 ? "text-red-400" : (summary.remaining_balance || 0) < 0 ? "text-emerald-400" : "text-muted-foreground";

  return (
    <div className="space-y-5" data-testid="tech-financials-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope']">My Financials</h1>
          <p className="text-muted-foreground text-sm">
            {data?.tech_profile?.name ? `${data.tech_profile.name} — ` : ""}
            Commission: {data?.tech_profile?.commission_rate ? `${data.tech_profile.commission_rate}%` : "—"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="w-[160px] h-9 border-theme text-sm" data-testid="date-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_FILTERS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9 border-theme" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <SummaryCard icon={CheckCircle} label="Completed Jobs"
              value={summary.total_jobs ?? 0}
              color="text-blue-400" bgColor="bg-blue-500/15" testId="card-total-jobs" />
            <SummaryCard icon={DollarSign} label="Total Revenue"
              value={fmt(summary.total_revenue)}
              color="text-green-400" bgColor="bg-green-500/15" testId="card-revenue" />
            <SummaryCard icon={TrendingUp} label="My Commission"
              value={fmt(summary.total_commission)}
              color="text-purple-400" bgColor="bg-purple-500/15" testId="card-commission" />
            <SummaryCard icon={Receipt} label="Net Earnings"
              value={fmt(summary.net_earnings)}
              color={netColor} bgColor="bg-emerald-500/15" testId="card-net" />
          </div>

          {/* Financial Breakdown */}
          <Card className="glass-card border-theme">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-foreground font-['Manrope'] mb-3">Earnings Breakdown</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Commission Earned</span>
                  <span className="text-purple-400 font-medium">{fmt(summary.total_commission)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground flex items-center gap-1"><ArrowDownRight className="w-3 h-3 text-red-400" /> Processing Fees</span>
                  <span className="text-red-400 font-medium">-{fmt(summary.processing_fees)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground flex items-center gap-1"><ArrowUpRight className="w-3 h-3 text-emerald-400" /> Parts Reimbursement</span>
                  <span className="text-emerald-400 font-medium">+{fmt(summary.tech_parts_reimbursement)}</span>
                </div>
                {(summary.total_adjustments || 0) !== 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Adjustments</span>
                    <span className={`font-medium ${(summary.total_adjustments || 0) > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(summary.total_adjustments || 0) > 0 ? '+' : ''}{fmt(summary.total_adjustments)}
                    </span>
                  </div>
                )}
                <div className="border-t border-theme pt-2 flex justify-between font-semibold">
                  <span className="text-foreground">Net Earnings</span>
                  <span className={netColor}>{fmt(summary.net_earnings)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Balance Summary */}
          <Card className="glass-card border-theme">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-foreground font-['Manrope'] mb-3">Balance Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cash Collected</span>
                  <span className="text-amber-400 font-medium">{fmt(summary.tech_collected)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Net Earnings</span>
                  <span className={`font-medium ${netColor}`}>{fmt(summary.net_earnings)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Owes Company</span>
                  <span className={`font-medium ${(summary.owes_company || 0) > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {fmt(Math.abs(summary.owes_company || 0))}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Settled</span>
                  <span className="text-cyan-400 font-medium">{fmt(summary.total_settled)}</span>
                </div>
                <div className="border-t border-theme pt-2 flex justify-between font-semibold">
                  <span className="text-foreground">Remaining Balance</span>
                  <span className={balColor}>{fmt(Math.abs(summary.remaining_balance || 0))}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Jobs Table */}
          <Card className="glass-card border-theme">
            <CardContent className="p-0">
              <div className="flex items-center justify-between p-4 border-b border-theme">
                <h2 className="text-sm font-semibold text-foreground font-['Manrope']">
                  Completed Jobs — {filterLabel}
                </h2>
                <Badge variant="outline" className="text-[10px] border-theme text-muted-foreground">
                  {jobs.length} job{jobs.length !== 1 ? "s" : ""}
                </Badge>
              </div>

              {jobs.length === 0 ? (
                <div className="p-12 text-center" data-testid="empty-state">
                  <BarChart3 className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
                  <p className="text-sm font-medium text-muted-foreground">No completed jobs for {filterLabel.toLowerCase()}</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">Try selecting a different date range</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="jobs-table">
                    <thead className="bg-muted/40">
                      <tr className="text-muted-foreground text-left text-[11px] border-b border-theme">
                        <th className="p-3">Job</th>
                        <th className="p-3">Customer</th>
                        <th className="p-3">Completed</th>
                        <th className="p-3 text-right">Revenue</th>
                        <th className="p-3 text-right">Commission</th>
                        <th className="p-3 text-right">Fees</th>
                        <th className="p-3 text-right">Parts Reimb.</th>
                        <th className="p-3 text-right">Collected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map((job, i) => (
                        <tr key={job.job_id || i} className="border-b border-theme/40 hover:bg-muted/10 transition-colors" data-testid={`fin-job-row-${i}`}>
                          <td className="p-3">
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs font-medium text-foreground truncate max-w-[180px]">{job.title || "Job"}</p>
                              {job.pending_payment && (
                                <span
                                  className="inline-flex items-center text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-500 border border-amber-500/30 whitespace-nowrap"
                                  title="Job is operationally complete but the customer hasn't paid yet. Money totals exclude this until payment is received."
                                  data-testid={`fin-pending-pill-${i}`}
                                >
                                  Pending payment
                                </span>
                              )}
                            </div>
                            {job.job_number && <p className="text-[10px] text-muted-foreground">#{job.job_number}</p>}
                          </td>
                          <td className="p-3 text-xs text-muted-foreground truncate max-w-[140px]">{job.customer_name || "—"}</td>
                          <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                            {job.completed_at ? format(parseISO(job.completed_at), "MMM d, yyyy") : "—"}
                          </td>
                          <td className="p-3 text-xs text-foreground text-right font-medium">{fmt(job.total_charged)}</td>
                          <td className="p-3 text-right">
                            <span className={`text-xs font-medium ${(job.commission_amount || 0) > 0 ? "text-purple-400" : "text-muted-foreground"}`}>
                              {fmt(job.commission_amount)}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            <span className={`text-xs font-medium ${(job.processing_fees || 0) > 0 ? "text-red-400" : "text-muted-foreground"}`}>
                              {fmt(job.processing_fees)}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            <span className={`text-xs font-medium ${(job.tech_parts_reimbursement || 0) > 0 ? "text-emerald-400" : "text-muted-foreground"}`}>
                              {fmt(job.tech_parts_reimbursement)}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            <span className={`text-xs font-medium ${(job.tech_collected || 0) > 0 ? "text-amber-400" : "text-muted-foreground"}`}>
                              {fmt(job.tech_collected)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-muted/30">
                      <tr className="text-xs font-semibold border-t border-theme">
                        <td className="p-3 text-foreground" colSpan={3}>Totals ({jobs.length} jobs)</td>
                        <td className="p-3 text-right text-foreground">{fmt(summary.total_revenue)}</td>
                        <td className="p-3 text-right text-purple-400">{fmt(summary.total_commission)}</td>
                        <td className="p-3 text-right text-red-400">{fmt(summary.processing_fees)}</td>
                        <td className="p-3 text-right text-emerald-400">{fmt(summary.tech_parts_reimbursement)}</td>
                        <td className="p-3 text-right text-amber-400">{fmt(summary.tech_collected)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default TechFinancials;
